<template>
  <w-setting-sliding-popup ref="slidingPopup" :title="`${popupParams.targetName} 설정`" width="1200px" @close="close">
    <div slot="content">
      <el-row type="flex">
        <div class="mr-r15" style="width: 50%">
          <el-row class="title-bar" type="flex" justify="space-between">
            <div class="vertical-center-box title-h4">관리자 목록</div>
            <w-header-search-button class="vertical-center-box" @click="showMgrSearchTool = !showMgrSearchTool"></w-header-search-button>
          </el-row>
          <el-table-wrapper :data="mgrList" :columns="tableColumns" :column-default="columnDefault" :show-search-tool="showMgrSearchTool" :show-custom-header="true"
          :pagination="true" :total="total" remote ref="mgrTable" @select="onSelect" @select-all="onSelectAll" @condition-change="loadData"
          @pagination-change="handlerPaginationChange" @total-change="handlerPaginationChange"></el-table-wrapper>
        </div>
        <div style="width: 50%">
          <el-row class="title-bar" type="flex" justify="space-between">
            <div class="vertical-center-box title-h4">{{ `${popupParams.targetName} 목록` }}</div>
            <div class="vertical-center-box">
              <el-button class="mr-r4" @click="onReleaseButtonClick">해제</el-button>
              <w-header-search-button @click="showTargetSearchTool = !showTargetSearchTool"></w-header-search-button>
            </div>
          </el-row>
          <el-table-wrapper :data="targetList" :columns="tableColumns" :column-default="columnDefault" :show-search-tool="showTargetSearchTool" :show-custom-header="true"
            :pagination="true" :total="total" ref="targetTable" @selection-change="changeCheckedRow"
            @pagination-change="handlerPaginationChange" @total-change="handlerPaginationChange"></el-table-wrapper>
        </div>
      </el-row>
    </div>
    <el-row slot="footer" type="flex" justify="center">
      <el-button class="mr-r4" size="large" type="solid" @click="onSaveButtonClick">저장</el-button>
      <el-button size="large" @click="close">취소</el-button>
    </el-row>
  </w-setting-sliding-popup>
</template>

<script>
import { isNil, difference, cloneDeep, map } from 'lodash';
import { columnDefault, defaultPagination } from '@/constants/table';
import { getValueAfterCheckEmpty } from '@/utils/string';
import msgUtil from '@/utils/message';
import mgrAPI from '@/api/model/mgr';
import provisioningSettingsAPI from '@/api/settings/virtual/provisioning-settings';

const defaultFormatter = (row, column, value) => getValueAfterCheckEmpty(value);
const pagination = cloneDeep(defaultPagination);

export default {
  name: 'WRequestTargetSettingPopup',
  props: {
    popupParams: {
      type: Object,
      default: () => ({ targetName: '' }),
    },
  },
  data() {
    return {
      total: 0,
      pagination,
      columnDefault,
      showMgrSearchTool: false,
      showTargetSearchTool: false,
      originTargetMgrIds: [],
      mgrList: [],
      targetList: [],
      checkedRows: [],
      tableColumns: [
        { type: 'selection' },
        { label: '아이디', prop: 'managerId', searchable: true, sortable: true, remoteProp: 'manager_id' },
        { label: '이름', prop: 'managerName', formatter: defaultFormatter, searchable: true, sortable: true, remoteProp: 'manager_name' },
        { label: '모바일', prop: 'mobileNumber', formatter: defaultFormatter, searchable: true, sortable: true, remoteProp: 'mobile_number' },
        { label: '이메일', prop: 'email', formatter: defaultFormatter, searchable: true, sortable: true },
      ],
    };
  },
  methods: {
    open() {
      this.$refs.slidingPopup.visible = true;
      this.loadData();
    },
    close() {
      this.$refs.slidingPopup.visible = false;
      this.$refs.mgrTable.getTable().clearSelection();
      this.$refs.targetTable.getTable().clearSelection();
      this.showMgrSearchTool = false;
      this.showTargetSearchTool = false;
    },
    /**
     * 관리자, 접수자 목록 로드
     * @author jeee12
     * @return {[type]} [description]
     */
    loadData() {
      const me = this;
      me.$nextTick(() => {
        const mgrTable = me.$refs.mgrTable;
        mgrAPI.getMgrList(mgrTable.getParameter()).then((response) => {
          if (response.data.success) {
            me.mgrList = response.data.data;
            me.targetList = cloneDeep(me.popupParams.targetList);
            me.originTargetMgrIds = map(me.targetList, (mgr) => mgr.managerId);
          }
        }).finally(() => {
          me.mgrList.forEach((mgr) => {
            const existMgr = me.targetList.find((e) => (e.managerId === mgr.managerId));
            mgrTable.getTable().toggleRowSelection(mgr, !isNil(existMgr));
          });
        });
      });
    },
    onSelect(selection) {
      this.targetList = selection;
    },
    onSelectAll(selection) {
      this.targetList = selection;
    },
    changeCheckedRow(selection) {
      this.checkedRows = selection;
    },
    /**
     * 해제 버튼 click
     * @author jeee12
     * @return {[type]} [description]
     */
    onReleaseButtonClick() {
      this.checkedRows.forEach((row) => {
        this.targetList.splice(this.targetList.indexOf(row), 1);
      });
      this.mgrList.forEach((mgr) => {
        const existMgr = this.targetList.find((e) => (e.managerId === mgr.managerId));
        if (isNil(existMgr)) {
          this.$refs.mgrTable.getTable().toggleRowSelection(mgr, false);
        }
      });
    },
    handlerPaginationChange(_pagination) {
      this.pagination = _pagination;
    },
    onSaveButtonClick() {
      const targetMgrIds = [];
      this.targetList.forEach((target) => {
        if (!targetMgrIds.includes(target.managerId)) {
          targetMgrIds.push(target.managerId);
        }
      });
      targetMgrIds.sort();
      const insertMgrIds = difference(targetMgrIds, this.originTargetMgrIds);
      const deleteMgrIds = difference(this.originTargetMgrIds, targetMgrIds);
      if (insertMgrIds.length === 0 && deleteMgrIds.length === 0) {
        this.close();
        return;
      }

      const taskId = this.popupParams.taskId;
      const reqTaskMgrList = [];
      const target = '처리자 설정';
      const action = msgUtil.action.SAVE;
      insertMgrIds.forEach((managerId) => {
        reqTaskMgrList.push({
          taskId,
          managerId,
          sql_type: 'INSERT',
        });
      });
      deleteMgrIds.forEach((managerId) => {
        reqTaskMgrList.push({
          taskId,
          managerId,
          sql_type: 'DELETE',
        });
      });

      provisioningSettingsAPI.updateVmsReqTaskMgrList(reqTaskMgrList).then((response) => {
        if (response.data.success) {
          this.$emit('reload', taskId);
          this.close();
        }
        this.$message(msgUtil.getResultMsgData(action, target, response.data.success));
      }).catch(() => {
        this.$message(msgUtil.getResultMsgData(action, target, false));
      });
    },
  },
};
</script>

<style module>
</style>



// WEBPACK FOOTER //
// src/views/settings/virtual/Provisioning/RequestProcess/popup/RequestTargetSettingPopup.vue